import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Video, Clock, CreditCard, Users, CheckCircle, ArrowRight, Lock, Eye, Bitcoin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Valoracion } from '../components/Valoracion';
import { Card, CardContent } from '../components/ui/card';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';

// Dummy professionals data
const professionals = [
  {
    id: 'psy-001',
    name: 'Dra. María Fernanda González',
    specialties: ['Ansiedad', 'Depresión', 'Estrés Laboral'],
    image: 'https://images.unsplash.com/photo-1645066928295-2506defde470?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxmcmllbmRseSUyMGRvY3RvciUyMHBvcnRyYWl0JTIwcHJvZmVzc2lvbmFsfGVufDB8fHx8MTc2OTc4OTg2MXww&ixlib=rb-4.1.0&q=85',
    rating: 4.9,
    price: 35000
  },
  {
    id: 'psy-002',
    name: 'Dr. Carlos Andrés Muñoz',
    specialties: ['Terapia de Pareja', 'Duelo', 'Trauma'],
    image: 'https://images.pexels.com/photos/8460157/pexels-photo-8460157.jpeg',
    rating: 4.8,
    price: 30000
  },
  {
    id: 'psy-003',
    name: 'Dra. Valentina Rojas',
    specialties: ['Adolescentes', 'TDAH', 'Autoestima'],
    image: 'https://images.unsplash.com/photo-1559839734-2b71ea197ec2?w=400',
    rating: 4.7,
    price: 28000
  },
  {
    id: 'psy-004',
    name: 'Dr. Sebastián Torres',
    specialties: ['Adicciones', 'Ansiedad Social', 'Fobias'],
    image: 'https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=400',
    rating: 4.9,
    price: 32000
  }
];

const testimonials = [
  {
    text: "Después de años de evitar buscar ayuda por miedo a que alguien supiera, AnonimaMente me dio la tranquilidad que necesitaba. Mi psicóloga nunca supo mi nombre real y eso me permitió abrirme completamente.",
    author: "Usuario anónimo",
    location: "Santiago"
  },
  {
    text: "La flexibilidad de horarios y el poder conectarme desde cualquier lugar cambió todo para mí. Ya no tengo excusas para no cuidar mi salud mental.",
    author: "Usuario anónimo",
    location: "Valparaíso"
  },
  {
    text: "Como profesional de alto perfil, la confidencialidad era mi mayor preocupación. AnonimaMente me ofreció exactamente lo que necesitaba: atención de calidad sin comprometer mi privacidad.",
    author: "Usuario anónimo",
    location: "Concepción"
  }
];

const HomePage = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="hero-bg relative min-h-[90vh] flex items-center">
        <div className="container-max mx-auto px-4 md:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8 animate-stagger">
              <div>
                <Badge className="bg-teal-100 text-teal-700 hover:bg-teal-100 mb-4">
                  <Lock className="w-3 h-3 mr-1" />
                  100% Anónimo y Confidencial
                </Badge>
                <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold text-slate-900 leading-tight">
                  Psicología anónima,
                  <br />
                  <span className="text-teal-600">profesional y segura</span>
                </h1>
              </div>
              
              <p className="text-lg md:text-xl text-slate-600 leading-relaxed max-w-xl">
                Cuida tu salud mental sin exponer tu identidad. Conecta con psicólogos verificados desde la comodidad de tu hogar, usando solo un seudónimo.
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/agendar">
                  <Button 
                    size="lg" 
                    className="btn-primary w-full sm:w-auto"
                    data-testid="hero-agendar-btn"
                  >
                    Quiero atención anónima
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
                <Link to="/empresas">
                  <Button 
                    size="lg" 
                    variant="outline"
                    className="btn-secondary w-full sm:w-auto"
                    data-testid="hero-empresas-btn"
                  >
                    Soy empresa / Quiero un programa EAP
                  </Button>
                </Link>
              </div>

              <div className="flex flex-wrap items-center gap-6 pt-4">
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-sage-500" />
                  <span>Sin grabación de sesiones</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-sage-500" />
                  <span>Profesionales verificados</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  <CheckCircle className="w-5 h-5 text-sage-500" />
                  <span>Pago en CLP o Bitcoin</span>
                </div>
              </div>
            </div>

            <div className="relative hidden lg:block">
              <div className="relative z-10">
                <img 
                  src="https://images.unsplash.com/photo-1546975313-c2a873d092d8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHwxfHxjYWxtJTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwZm9nJTIwZm9yZXN0JTIwbGFrZXxlbnwwfHx8fDE3Njk3ODk4NTV8MA&ixlib=rb-4.1.0&q=85"
                  alt="Paisaje tranquilo de lago con neblina"
                  className="rounded-3xl shadow-2xl w-full object-cover h-[500px]"
                />
              </div>
              <div className="absolute -bottom-6 -left-6 z-20">
                <Card className="glass-card p-6 max-w-xs">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-full bg-sage-100 flex items-center justify-center">
                      <Shield className="w-6 h-6 text-sage-600" />
                    </div>
                    <div>
                      <p className="font-semibold text-slate-900">Tu identidad protegida</p>
                      <p className="text-sm text-slate-500">Solo usas un seudónimo</p>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* How it Works */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              Cómo funciona
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              En solo 3 simples pasos, accede a atención psicológica profesional manteniendo tu anonimato completo.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Step 1 */}
            <div className="relative">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-teal-100 flex items-center justify-center mx-auto">
                  <Eye className="w-8 h-8 text-teal-600" />
                </div>
                <div className="absolute top-8 left-1/2 w-full h-0.5 bg-teal-100 hidden md:block" style={{ transform: 'translateX(50%)' }} />
                <span className="inline-block w-8 h-8 rounded-full bg-teal-600 text-white font-semibold text-sm leading-8">1</span>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Elige un seudónimo</h3>
                <p className="text-slate-600">
                  Crea un nombre ficticio o te asignamos uno automáticamente. Tu identidad real nunca será revelada.
                </p>
              </div>
            </div>

            {/* Step 2 */}
            <div className="relative">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-sage-100 flex items-center justify-center mx-auto">
                  <Clock className="w-8 h-8 text-sage-600" />
                </div>
                <div className="absolute top-8 left-1/2 w-full h-0.5 bg-teal-100 hidden md:block" style={{ transform: 'translateX(50%)' }} />
                <span className="inline-block w-8 h-8 rounded-full bg-sage-600 text-white font-semibold text-sm leading-8">2</span>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Agenda tu sesión</h3>
                <p className="text-slate-600">
                  Elige al profesional que mejor se adapte a ti y selecciona el horario que te convenga.
                </p>
              </div>
            </div>

            {/* Step 3 */}
            <div className="relative">
              <div className="text-center space-y-4">
                <div className="w-16 h-16 rounded-full bg-sand-200 flex items-center justify-center mx-auto">
                  <Video className="w-8 h-8 text-sand-700" />
                </div>
                <span className="inline-block w-8 h-8 rounded-full bg-sand-600 text-white font-semibold text-sm leading-8">3</span>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Conéctate por video</h3>
                <p className="text-slate-600">
                  Únete a tu sesión desde cualquier dispositivo. Cámara opcional para mantener tu anonimato visual.
                </p>
              </div>
            </div>
          </div>

          <div className="text-center mt-12">
            <Link to="/agendar">
              <Button className="btn-primary" data-testid="how-it-works-cta">
                Comenzar ahora
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="section-padding bg-slate-50">
        <div className="container-max mx-auto">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
              ¿Por qué elegir AnonimaMente?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Diseñamos cada aspecto de nuestra plataforma pensando en tu privacidad y bienestar.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center">
                  <Lock className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Anonimato real</h3>
                <p className="text-slate-600">
                  No pedimos tu RUT, nombre real ni datos personales. Solo un seudónimo para tu sesión.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-sage-100 flex items-center justify-center">
                  <CheckCircle className="w-6 h-6 text-sage-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Profesionales verificados</h3>
                <p className="text-slate-600">
                  Todos nuestros psicólogos están titulados y con registro profesional verificado.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-sand-200 flex items-center justify-center">
                  <Video className="w-6 h-6 text-sand-700" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Sin grabación</h3>
                <p className="text-slate-600">
                  Las sesiones nunca se graban. Lo que hablas con tu psicólogo queda solo entre ustedes.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-teal-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-teal-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Horarios flexibles</h3>
                <p className="text-slate-600">
                  Sesiones disponibles de lunes a domingo, en horarios que se adaptan a tu rutina.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-sage-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-sage-600" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Múltiples pagos</h3>
                <p className="text-slate-600">
                  Paga con Webpay, Flow, Khipu o incluso Bitcoin para mayor privacidad.
                </p>
              </CardContent>
            </Card>

            <Card className="glass-card card-hover p-8">
              <CardContent className="p-0 space-y-4">
                <div className="w-12 h-12 rounded-2xl bg-sand-200 flex items-center justify-center">
                  <Bitcoin className="w-6 h-6 text-sand-700" />
                </div>
                <h3 className="font-heading text-xl font-semibold text-slate-900">Pago con Bitcoin</h3>
                <p className="text-slate-600">
                  Para quienes buscan máxima privacidad, aceptamos Bitcoin via BTCPay Server.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Professionals Grid */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-4">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-4">
                Conoce a nuestros profesionales
              </h2>
              <p className="text-lg text-slate-600 max-w-2xl">
                Psicólogos titulados, verificados y comprometidos con tu bienestar.
              </p>
            </div>
            <Link to="/profesionales">
              <Button variant="outline" className="shrink-0">
                Ver todos
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {professionals.map((pro) => (
              <Card key={pro.id} className="professional-card overflow-hidden border-0 shadow-soft">
                <div className="aspect-[4/3] overflow-hidden">
                  <img 
                    src={pro.image} 
                    alt={pro.name}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                </div>
                <CardContent className="p-6">
                  <Valoracion
                    rating={pro.rating}
                    cantidad={pro.rating_count}
                    className="mb-2"
                  />
                  <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                    {pro.name}
                  </h3>
                  <div className="flex flex-wrap gap-1 mb-4">
                    {pro.specialties.slice(0, 2).map((spec) => (
                      <Badge key={spec} variant="secondary" className="text-xs bg-teal-50 text-teal-700">
                        {spec}
                      </Badge>
                    ))}
                  </div>
                  <div className="flex items-center justify-between">
                    <p className="text-sm text-slate-500">
                      Desde <span className="font-semibold text-slate-900">${pro.price.toLocaleString('es-CL')}</span>
                    </p>
                    <Link to={`/agendar?profesional=${pro.id}`}>
                      <Button size="sm" variant="ghost" className="text-teal-600 hover:text-teal-700">
                        Agendar
                      </Button>
                    </Link>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-teal-600">
        <div className="container-max mx-auto">
          <div className="text-center mb-12">
            <h2 className="font-heading text-3xl md:text-4xl font-semibold text-white mb-4">
              Lo que dicen nuestros usuarios
            </h2>
            <p className="text-lg text-teal-100 max-w-2xl mx-auto">
              Historias reales de personas que encontraron ayuda manteniendo su privacidad.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="bg-white/10 backdrop-blur-sm border-white/20 p-8">
                <CardContent className="p-0">
                  <div className="testimonial-card">
                    <p className="text-white/90 mb-6 leading-relaxed">
                      "{testimonial.text}"
                    </p>
                    <div className="flex items-center gap-3">
                      <Avatar className="w-10 h-10 bg-teal-500">
                        <AvatarFallback className="bg-teal-500 text-white text-sm">
                          {testimonial.author[0]}
                        </AvatarFallback>
                      </Avatar>
                      <div>
                        <p className="font-medium text-white">{testimonial.author}</p>
                        <p className="text-sm text-teal-100">{testimonial.location}</p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Teaser */}
      <section className="section-padding bg-sage-50">
        <div className="container-max mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="bg-sage-100 text-sage-700 hover:bg-sage-100 mb-4">
                <Users className="w-3 h-3 mr-1" />
                Para Empresas
              </Badge>
              <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
                Programa EAP anónimo para tu equipo
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Ofrece apoyo psicológico a tus colaboradores sin comprometer su privacidad. Mayor adopción, menos estigma, mejor bienestar organizacional.
              </p>

              <ul className="space-y-4 mb-8">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Reportes solo agregados (n≥10)</p>
                    <p className="text-sm text-slate-600">Nunca identidades individuales</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">SLA primera sesión ≤72h</p>
                    <p className="text-sm text-slate-600">Premium ≤48h</p>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-sage-500 shrink-0 mt-0.5" />
                  <div>
                    <p className="font-medium text-slate-900">Implementación en 3-4 semanas</p>
                    <p className="text-sm text-slate-600">Onboarding completo incluido</p>
                  </div>
                </li>
              </ul>

              <Link to="/empresas">
                <Button className="btn-sage" data-testid="b2b-cta">
                  Conocer programa empresas
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
              </Link>
            </div>

            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1760592150401-58572faf676c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzR8MHwxfHNlYXJjaHw0fHxjYWxtJTIwbmF0dXJlJTIwbGFuZHNjYXBlJTIwZm9nJTIwZm9yZXN0JTIwbGFrZXxlbnwwfHx8fDE3Njk3ODk4NTV8MA&ixlib=rb-4.1.0&q=85"
                alt="Muelle en lago tranquilo"
                className="rounded-3xl shadow-xl w-full object-cover h-[400px]"
                loading="lazy"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-padding bg-white">
        <div className="container-max mx-auto text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-semibold text-slate-900 mb-6">
            Tu bienestar mental merece atención
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-8">
            Da el primer paso hacia una mejor salud mental. Sin juicios, sin exposición, solo apoyo profesional cuando lo necesites.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/agendar">
              <Button size="lg" className="btn-primary" data-testid="final-cta-agendar">
                Agendar mi primera sesión
                <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <Link to="/personas">
              <Button size="lg" variant="outline" className="btn-secondary">
                Conocer más
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
